import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import EmptyState from '../../../components/EmptyState';
import Button from '../../../components/Button';
import type { TriggerLog } from '../services/triggerLogTypes';

interface TriggerLogsListProps {
    triggerLogs: TriggerLog[];
    isLoading: boolean;
    isCurrentUser: boolean;
    onClearAll: () => Promise<void>;
}

export const TriggerLogsList = ({ triggerLogs, isLoading, isCurrentUser, onClearAll }: TriggerLogsListProps) => {
    const { formatDate } = useFormatUtils();
    const [isClearing, setIsClearing] = useState(false);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-gray-600">Loading trigger logs...</div>
            </div>
        );
    }

    if (triggerLogs.length === 0) {
        return <EmptyState title="No trigger logs" description="No trigger logs found for this user" />;
    }

    const formatDuration = (minutes: number): string => {
        if (minutes < 60) {
            return `${minutes} min`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const handleClearAll = async () => {
        const confirmed = window.confirm(
            `Are you sure you want to delete all ${triggerLogs.length} trigger logs? This action cannot be undone.`
        );

        if (!confirmed) {
            return;
        }

        setIsClearing(true);
        try {
            await onClearAll();
        } catch (error) {
            console.error('Error clearing trigger logs:', error);
            alert('Failed to clear trigger logs. Please try again.');
        } finally {
            setIsClearing(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                    Trigger Logs ({triggerLogs.length})
                </h2>
                {isCurrentUser && triggerLogs.length > 0 && (
                    <Button
                        onClick={handleClearAll}
                        variant="danger"
                        icon={<Trash2 className="w-4 h-4" />}
                        disabled={isClearing}
                    >
                        {isClearing ? 'Clearing...' : 'Clear All'}
                    </Button>
                )}
            </div>
            <div className="space-y-4">
                {triggerLogs.map((log) => (
                    <div
                        key={log.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                        <div className="flex flex-wrap justify-between items-start gap-x-2 mb-3">
                            <span className="text-sm text-gray-500">
                                {formatDate(log.createdAt.getTime())}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                                Duration: {formatDuration(log.duration)}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Obsession */}
                            <div className="border-l-4 border-purple-500 pl-3">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Obsession
                                </h3>
                                {log.obsession.selectedOption && (
                                    <p className="text-xs text-gray-500 mb-1">
                                        Option: {log.obsession.selectedOption}
                                    </p>
                                )}
                                <p className="text-sm text-gray-900">
                                    {log.obsession.customDescription || (
                                        <span className="text-gray-400 italic">No description</span>
                                    )}
                                </p>
                            </div>

                            {/* Compulsion */}
                            <div className="border-l-4 border-blue-500 pl-3">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Compulsion
                                </h3>
                                {log.compulsion.selectedOption && (
                                    <p className="text-xs text-gray-500 mb-1">
                                        Option: {log.compulsion.selectedOption}
                                    </p>
                                )}
                                <p className="text-sm text-gray-900">
                                    {log.compulsion.customDescription || (
                                        <span className="text-gray-400 italic">No description</span>
                                    )}
                                </p>
                            </div>

                            {/* Trigger */}
                            <div className="border-l-4 border-orange-500 pl-3">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Trigger
                                </h3>
                                {log.trigger.selectedOption && (
                                    <p className="text-xs text-gray-500 mb-1">
                                        Option: {log.trigger.selectedOption}
                                    </p>
                                )}
                                <p className="text-sm text-gray-900">
                                    {log.trigger.customDescription || (
                                        <span className="text-gray-400 italic">No description</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
